/**
 * Lobby2.0 Application Entry Point
 * Mimics the original lobby's reactLobbyApp pattern with modern architecture
 */

import React from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { Subject } from '../core/utils/Subject';
import App from '../layouts/App';
import { Providers } from '../core/providers/Providers';
import { useAppStore, deriveCampaignViewState } from '../store/app/appStore';
import { externalCommunication } from '../core/services/communication/ExternalCommunication';
import type { IExternalChannel } from '../types/external/IExternalChannel';
import type { ILobbyOptions } from '../types/external/ILobbyOptions';
import { normalizeLanguageCode } from '../core/utils/languageUtils';
import type { IOptions } from '../types/external/IOptions';
import IFrameApplication from './IFrameApplication';
import type IFrameApplicationLite from '../iframe-loader/IFrameApplicationLite';
import type { reactLobby as reactLobbyLite } from '../iframe-loader';
import '../../i18n'; // Initialize i18next
import packageJson from '../../package.json';
import { trackerManager } from '../core/services/managers/TrackerManager';
import { wireExternalMessages } from '../core/services/communication/wireExternalMessages';
import { startLobbyConfigDiagnostics } from './lobbyConfigDiagnostics/lobbyConfigDiagnostics';
import type { JackpotPanelAnimationManager } from 'core/services/managers/JackpotPanelAnimationManager';

wireExternalMessages();


// QA Panel — dev tool. Two-gate check, both must pass:
//
//   1. DEPLOY_ENV allowlist — every local, dev, QA and experimental deploy
//      target. The list lives in config/webpack.config.js (qaPanelAllowed),
//      which resolves it at build time from the Jenkins DEPLOY_ENV parameter
//      and injects process.env.QA_PANEL_ALLOWED as the literal string 'true'
//      or 'false'. Anything not on the list (RELEASE, DEMO, CUSTOM*, UNKNOWN,
//      unset, or any future value) is excluded by default — fail-closed.
//
//   2. Version contains "rc" (release-candidate suffix). Belt-and-
//      suspenders fallback: even if a non-prod DEPLOY_ENV ships
//      accidentally with a final-release version, the panel stays off.
//
// The gate is intentionally a single `===` against a DefinePlugin literal,
// NOT `[...].includes(deployEnv)` or a regex here. Terser refuses to
// constant-fold Array.prototype.includes (the prototype could be
// monkey-patched), so that form leaves the branch "live" and the dynamic
// import gets pulled into the prod bundle. With the literal comparison the
// condition folds, and the entire branch is dead-stripped in non-allowlisted
// builds. Short-circuit `&&` means the version check is skipped at compile
// time too — no need to make it foldable.
if (process.env['QA_PANEL_ALLOWED'] === 'true' && packageJson.version.includes('rc')) {
  import('../features/qaPanel').then((m) => m.initQaPanel());
}
// Type definition for SSI configuration
interface SSIConfig {
  lobbyHost?: string;
  apiIntegrationHost?: string;
  assetsHost?: string;
  assetsHash?: string;
  redirectHost?: string;
  tournamentHost?: string;
  prizePopServiceHost?: string;
  rebateHost?: string;
  isAggregator?: boolean;
  sentryEnvironment?: string;
}

// Extend Window interface to include lobbySsi and other global properties.
// reactLobby / lobby2IFrameApp are populated by two different bundles that
// never coexist in the same window: the full app (this file) and the
// lightweight iframe-loader — hence the unions.
declare global {
  interface Window {
    lobbySsi?: SSIConfig;
    lobby2App?: Lobby2Application;
    lobby2IFrameApp?: IFrameApplication | IFrameApplicationLite;
    reactLobby?: typeof reactLobby | typeof reactLobbyLite;
    reactLobbyApp?: typeof reactLobbyApp;
    reactLobbyUnmount?: typeof reactLobbyUnmount;
    jackpotPanelAnimationManager?: JackpotPanelAnimationManager;
  }
}

// Utility function to normalize lobby options
const normalizeOptions = (options: ILobbyOptions): ILobbyOptions => {
  // Normalize device mode
  if (typeof options.mode === 'string' && options.mode.length) {
    const mode = options.mode.toLowerCase();
    if (mode === 'mobile' || mode === 'desktop') {
      options.mode = mode;
    }
  }

  // Normalize language code
  if (typeof options.languageCode === 'string' && options.languageCode.length) {
    options.languageCode = options.languageCode.toLowerCase();
  }

  return options;
};

class Lobby2Application {
  public element: HTMLElement;
  public channel: Subject<IExternalChannel>;
  private reactRoot: Root | null = null;
  private options: IOptions;
  private assetsHashCache: Map<string, Promise<string>> = new Map();

  constructor(options: IOptions, rootElementId: string) {
    trackerManager.startStep('Lobby2Application initialization');
    this.options = options;
    this.element = this.getRootElement(rootElementId);
    this.channel = new Subject<IExternalChannel>();

    // Connect our internal channel to external communication
    this.setupChannelBridge();
  }

  private getRootElement(elementId: string): HTMLElement {
    let root = document.getElementById(elementId);
    if (!root) {
      root = document.createElement('div');
      root.setAttribute('id', elementId);
      document.body.appendChild(root);
    }
    return root;
  }

  private hasValidSSI(): boolean {
    return window.lobbySsi !== undefined && typeof window.lobbySsi === 'object';
  }

  private async loadFontsAsync() {
    // Dynamically import to avoid circular dependencies
    const { loadFontsFromAssets } = await import('../core/services/managers/FontLoader');
    loadFontsFromAssets({
      assetsHost: this.options.assetsHost,
      assetsHash: this.options.assetsHash || '',
    });
  }

  private setupChannelBridge() {
    // Bridge between our new communication system and the expected CHANNEL
    this.channel.subscribe((message) => {
      // Don't echo incoming messages back to the parent
      if ('requestType' in message && message.requestType === 'incoming') {
        return;
      }
      externalCommunication.sendMessage(message.event, message.value);
    });

    // Setup iframe message listener (like old lobby Application.tsx)
    window.addEventListener('message', (event) => {
      if (event.data?.requestType === 'outgoing') {
        return;
      }
      if (event.data.type && event.data.type.indexOf('webpack') !== -1) {
        return;
      }

      // Forward to channel with requestType to prevent loops
      this.channel.next({
        ...event.data,
        requestType: 'incoming',
      });
    });

    // Send lobbyInitialized event immediately (like old lobby Application.tsx lines 127-135)
    // This enables the tournament cup in the game client
    this.propagateInternalEvents();
  }

  private propagateInternalEvents() {
    // Send lobbyInitialized event to parent window (game client)
    // This is critical for enabling tournament cup functionality
    externalCommunication.sendLobbyInitialized();
  }

  private _sanitizeOptions(options: ILobbyOptions): IOptions {
    const sanitizedOptions: IOptions = {
      playerId: options.playerId,
      gameKey: options.gameKey,
      languageCode: normalizeLanguageCode(options.languageCode),
      mode: options.mode,
      currency: options.currency,
      units: options.units,
      openType: options.openType,
      balance: options.balance,
      closeUrl: options.closeUrl || '',
      sendOpenLobbyEvent: options.sendOpenLobbyEvent === undefined ? true : options.sendOpenLobbyEvent,
      mute: Boolean(options.mute),
      bgMusicMute: Boolean(options.bgMusicMute),
      sessionKey: options.sessionKey || '',
      mountLobby: options.mountLobby === undefined ? true : options.mountLobby,
      singleView: options.singleView || '',
      campaigns: options.campaigns || [],
      // Required ISettings properties
      host: '',
      apiHost: '',
      assetsHost: '',
      redirectHost: '',
      isSettingsModalOpen: false,
      isSearchOpen: false,
      visible: true,
      tournamentHost: '',
      prizePopHost: '',
      rebateHost: '',
      theme: 'default',
      isAggregator: false,
      restrictions: [],
    };

    // Handle SSI configuration (Server Side Includes)
    if (this.hasValidSSI()) {
      const ssi: SSIConfig = window.lobbySsi as SSIConfig;

      // Found SSI configuration

      sanitizedOptions.host = ssi.lobbyHost || options.host || '';
      sanitizedOptions.apiHost = ssi.apiIntegrationHost || options.apiHost || '';
      sanitizedOptions.assetsHost = ssi.assetsHost || options.assetsHost || '';
      sanitizedOptions.assetsHash = ssi.assetsHash || options.assetsHash || '';
      // SSI stays the source of truth (aggregators redirect through a game-launcher, not the client).
      // Exception: an experimental client — the casino's SSI points at the regular QA client instead.
      const isExperimentalClient = Boolean(options.redirectHost?.includes('experimental'));
      sanitizedOptions.redirectHost = isExperimentalClient
        ? options.redirectHost || ''
        : ssi.redirectHost || options.redirectHost || '';
      sanitizedOptions.tournamentHost = ssi.tournamentHost || options.tournamentHost || '';
      sanitizedOptions.prizePopHost = ssi.prizePopServiceHost || '';
      sanitizedOptions.rebateHost = ssi.rebateHost || options.rebateHost || '';
      sanitizedOptions.isAggregator = ssi.isAggregator || options.isAggregator || false;

      // Using SSI configuration for assets
    } else {
      // No SSI configuration found, using environment/options fallbacks

      // Use environment variables as fallbacks (like old lobby)
      sanitizedOptions.host = options.host || process.env['LOBBY_HOST'] || '';
      sanitizedOptions.apiHost = options.apiHost || process.env['API_INTEGRATION_HOST'] || '';
      sanitizedOptions.assetsHost = options.assetsHost || process.env['ASSETS_HOST'] || '';
      sanitizedOptions.assetsHash = options.assetsHash || '';
      sanitizedOptions.redirectHost = options.redirectHost || process.env['LOBBY_REDIRECT_HOST'] || '';
      sanitizedOptions.tournamentHost = options.tournamentHost || process.env['TOURNAMENT_HOST'] || '';
      sanitizedOptions.prizePopHost = options.prizePopHost || process.env['PRIZE_POP_HOST'] || '';
      sanitizedOptions.rebateHost = options.rebateHost || process.env['REBATE_HOST'] || '';
      sanitizedOptions.isAggregator = options.isAggregator || process.env['IS_AGGREGATOR'] === 'true' || false;

      // Emergency fallback for experimental environments only if no SSI was found
      // This should rarely be needed if SSI is working properly
      const currentHost = window.location.hostname;
      if (!sanitizedOptions.assetsHost && currentHost.includes('experimental')) {
        sanitizedOptions.assetsHost = 'https://lobby-dev-cdn.egt-ong.com';
        // Emergency fallback: Detected experimental environment
      }
      if (!sanitizedOptions.redirectHost && currentHost.includes('experimental')) {
        // Extract the experimental number and construct the redirect host
        const expMatch = currentHost.match(/experimental(\d+)/);
        if (expMatch) {
          sanitizedOptions.redirectHost = `https://game-client-experimental${expMatch[1]}.egtnext.com`;
          // Emergency fallback: Using experimental redirect host
        }
      }

      // Using environment/options for assets
    }

    return sanitizedOptions;
  }

  private addPreconnectLink(assetsHost: string, assetsHash?: string): void {
    // Create preconnect URL
    const host = assetsHash ? `${assetsHost}/${assetsHash}` : assetsHost;

    // Check if preconnect already exists
    const existingPreconnect = document.querySelector(`link[rel="preconnect"][href="${host}"]`);
    if (existingPreconnect) return;

    // Create preconnect link with crossorigin attribute for CORS
    const preconnectLink = document.createElement('link');
    preconnectLink.setAttribute('rel', 'preconnect');
    preconnectLink.setAttribute('href', host);
    preconnectLink.setAttribute('crossorigin', ''); // Critical for CORS with CDN
    document.head.appendChild(preconnectLink);

    // Preconnect link added for CDN assets
  }

  private async loadAssetsHash(assetsHost: string, hashParam?: string): Promise<string> {
    if (hashParam) {
      return hashParam;
    }

    // Create cache key based on assetsHost
    const cacheKey = assetsHost;

    // Check if we already have a pending or completed request for this host
    const cachedHashPromise = this.assetsHashCache.get(cacheKey);
    if (cachedHashPromise) {
      // Return the existing promise (either pending or resolved)
      return cachedHashPromise;
    }

    // Create new promise and cache it immediately
    const hashPromise = (async () => {
      let hash = '';
      try {
        const version: string = process.env['VERSION'] || '';
        const statsUrl = `${assetsHost}${version ? '/' + version : ''}/assets/stats.json`;
        // Loading assets hash from CDN

        const response = await fetch(`${statsUrl}?timestamp=${Date.now()}`);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        hash = data.hash || '';
        // Assets hash loaded successfully
      } catch (error) {
        // Failed to load assets hash
        // Remove from cache on error to allow retry
        this.assetsHashCache.delete(cacheKey);
        throw error;
      }
      return hash;
    })();

    // Cache the promise
    this.assetsHashCache.set(cacheKey, hashPromise);

    return hashPromise;
  }

  public async init(): Promise<void> {
    // Initialize the Zustand store with the provided options
    const { updateSettings, updateBalance } = useAppStore.getState();

    // Import FontLoader and trigger font loading early
    if (this.options.assetsHost) {
      this.loadFontsAsync();
    }

    // Set initial settings and balance immediately to unblock UI
    const initialSettings = {
      ...this.options,
      languageCode: normalizeLanguageCode(this.options.languageCode),
      assetsHash: this.options.assetsHash || '', // Use provided or empty string
    };

    updateSettings(initialSettings);
    updateBalance({
      balance: this.options.balance,
      currency: this.options.currency,
      units: this.options.units,
    });

    // Derive the initial selectedSection/viewMode from the campaign settings before
    // the first render, so a campaign-only open (e.g. via the in-game cup) renders
    // the campaign view directly instead of flashing the multi-view lobby first.
    useAppStore.setState(deriveCampaignViewState(initialSettings));

    // Add preconnect for CDN assets if not localhost
    if (initialSettings.assetsHost && !initialSettings.assetsHost.includes('localhost')) {
      this.addPreconnectLink(initialSettings.assetsHost, initialSettings.assetsHash);

      // Load assets hash immediately when using CDN (crucial for development)
      this.loadAssetsHash(initialSettings.assetsHost, initialSettings.assetsHash)
        .then((assetsHash) => {
          if (assetsHash) {
            // Assets hash loaded successfully
            updateSettings({ assetsHash });
            // Update preconnect link with assets hash
            this.addPreconnectLink(initialSettings.assetsHost, assetsHash);
          }
        })
        .catch((error) => {
          console.error('Failed to load assets hash:', error);
        });
    }

    // Delayed SSI check - sometimes SSI injection happens after initial load
    setTimeout(() => {
      if (this.hasValidSSI()) {
        // Delayed SSI detection - found configuration
        // Re-sanitize options with SSI data
        const updatedOptions = this._sanitizeOptions({ ...this.options } as ILobbyOptions);
        const currentSettings = useAppStore.getState().settings;

        // Preserve existing sessionKey if it exists and is not empty
        if (currentSettings.sessionKey && currentSettings.sessionKey !== '') {
          updatedOptions.sessionKey = currentSettings.sessionKey;
        }

        // Only update if hosts have changed
        const hostsChanged =
          updatedOptions.assetsHost !== currentSettings.assetsHost ||
          updatedOptions.apiHost !== currentSettings.apiHost ||
          updatedOptions.host !== currentSettings.host ||
          updatedOptions.prizePopHost !== currentSettings.prizePopHost ||
          updatedOptions.tournamentHost !== currentSettings.tournamentHost ||
          updatedOptions.rebateHost !== currentSettings.rebateHost;

        if (hostsChanged) {
          updateSettings(updatedOptions);

          // Load assets hash from CDN when SSI is available (production/experimental)
          // Skip localhost (development uses local assets without hash)
          if (updatedOptions.assetsHost && !updatedOptions.assetsHost.includes('localhost')) {
            // Loading assets hash from CDN (will reuse cached promise if already fetching)
            this.loadAssetsHash(updatedOptions.assetsHost, updatedOptions.assetsHash)
              .then((assetsHash) => {
                if (assetsHash) {
                  // Assets hash loaded successfully
                  updateSettings({ assetsHash });
                  // Update preconnect link with assets hash
                  this.addPreconnectLink(updatedOptions.assetsHost, assetsHash);
                }
              })
              .catch(() => {
                // Failed to load assets hash
              });
          }
        }
      }

      // SSI has been applied (or is absent) — log any configuration problems.
      startLobbyConfigDiagnostics(this.options);
    }, 100);

    // Note: Assets hash loading moved to delayed SSI check to ensure proper host

    // Note: Settings request will be handled by the useInitializeLobby hook
    // This allows the app to render while settings are being fetched
  }

  public render(): void {
    if (!this.reactRoot) {
      this.reactRoot = createRoot(this.element);
    }
    trackerManager.completeStep('Lobby2Application initialization');
    this.reactRoot.render(
      <React.StrictMode>
        <Providers>
          <App />
        </Providers>
      </React.StrictMode>,
    );
  }

  public destroy(): void {
    if (this.reactRoot) {
      this.reactRoot.unmount();
      this.reactRoot = null;
    }

    if (this.element.parentNode) {
      this.element.parentNode.removeChild(this.element);
    }
  }
}

/**
 * IFrame mode entry point - called by Game Client
 * Creates an iframe that loads the lobby
 */
export const reactLobby = (
  rootElementId: string,
  options: ILobbyOptions,
): {
  element: HTMLElement;
  CHANNEL: Subject<IExternalChannel>;
} => {
  const normalizedOptions = normalizeOptions(options);
  const app = new IFrameApplication(normalizedOptions as IOptions, rootElementId);
  app.renderIframeApplication();

  // Store app instance globally for debugging
  window.lobby2IFrameApp = app;

  return {
    element: app.element,
    CHANNEL: app.channel,
  };
};

/**
 * Main entry point that matches the original lobby's API
 * Called from index.html with the same signature as the original
 */
export const reactLobbyApp = (
  rootElementId: string,
  options: ILobbyOptions,
): {
  element: HTMLElement;
  CHANNEL: Subject<IExternalChannel>;
} => {
  // Note: Sentry initialization moved to DISPLAY event handler in ExternalCommunication
  // This ensures session tracking only starts when user actually opens the lobby

  const normalizedOptions = normalizeOptions(options);
  const app = new Lobby2Application(normalizedOptions as IOptions, rootElementId);

  // Initialize and render
  app.init().then(() => {
    app.render();
  });

  // Store app instance globally for debugging
  window.lobby2App = app;

  return {
    element: app.element,
    CHANNEL: app.channel,
  };
};

/**
 * Unmount function that matches the original lobby's API
 */
export const reactLobbyUnmount = (_rootElementId?: string): void => {
  if (window.lobby2App) {
    window.lobby2App.destroy();
    delete window.lobby2App;
  }
};

// Expose functions globally to match original lobby pattern

trackerManager.startStep('IFrameApplication initialization');
window.reactLobby = reactLobby;
trackerManager.completeStep('IFrameApplication initialization');
window.reactLobbyApp = reactLobbyApp;
window.reactLobbyUnmount = reactLobbyUnmount;

// Load sound system test utilities in development
if (process.env['NODE_ENV'] === 'development') {
  import('../shared/utils/testSoundSystem').catch((error) => {
    console.warn('[LobbyApp] Failed to load sound test utilities:', error);
  });
}
